import { RevisionSingleActionProps } from 'components/tables/data/revision/types';
import { noop } from 'lodash';
import { Loader, Menu, Tooltip } from '@mantine/core';
import { IconFileDownload } from '@tabler/icons-react';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { useState } from 'react';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

/**
 * Action to re-fetch the archive clause for a signed KEP revision.
 */
export default function GetSignedDocumentAgainAction({
  revision,
  onStatusChange = noop,
  kind,
}: RevisionSingleActionProps) {
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(false);

  /**
   * Calls the RevisionGetArchiveClause API action.
   */
  const getSignedDocumentAgain = async () => {
    setLoading(true);
    try {
      const getArchiveClause = getAction('RevisionGetArchiveClause');
      await getArchiveClause({
        parameters: { revisionId: String(revision.revisionId) },
      });

      onStatusChange({
        status: revision.revisionStatus,
        revisionId: revision.revisionId,
      });
    } catch (error: any) {
      panic(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Opens a confirmation modal before running the action.
   */
  const confirmGetSignedDocumentAgain = () =>
    confirm({
      title: 'Stiahnutie podpisovej doložky',
      content: 'Naozaj si prajete stiahnuť podpisovú doložku?',
      onConfirm: getSignedDocumentAgain,
    });

  if (kind === 'action-icon') {
    return (
      <Tooltip label="Stiahnuť doložku" events={{ hover: true, focus: true, touch: true }}>
        <ActionIcon variant="secondary" size="md" onClick={confirmGetSignedDocumentAgain} loading={loading}>
          {loading ? <Loader size={24} /> : <IconFileDownload stroke={1.5} size={24} />}
        </ActionIcon>
      </Tooltip>
    );
  }

  if (kind === 'button') {
    return (
      <Button
        size="md"
        onClick={confirmGetSignedDocumentAgain}
        leftIcon={loading ? <Loader size={24} /> : <IconFileDownload stroke={1.5} size={24} />}
        variant="secondary"
        w={188}
        loading={loading}
      >
        Stiahnuť doložku
      </Button>
    );
  }

  return (
    <Menu.Item
      color="gray.8"
      onClick={confirmGetSignedDocumentAgain}
      icon={loading ? <Loader size={24} /> : <IconFileDownload stroke={1.5} size={24} />}
      disabled={loading}
    >
      Stiahnuť doložku
    </Menu.Item>
  );
}
